import React, { useEffect, useState } from 'react';

const REGIONS = [
  { code: 'TR', name: 'Turkey' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'IN', name: 'India' },
];

const getApiBase = () => {
  if (typeof window === 'undefined') return 'https://api.glowytics.com';
  const h = window.location?.hostname || '';
  return (h === 'localhost' || h === '127.0.0.1') ? '' : 'https://api.glowytics.com';
};

export default function YouTubePopularity() {
  const [tab, setTab] = useState('popular');
  const [region, setRegion] = useState('TR');
  const [maxResults, setMaxResults] = useState(24);
  const [categories, setCategories] = useState([]); // [{id,title}]
  const [categoryId, setCategoryId] = useState(''); // '' => All
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [topChannels, setTopChannels] = useState([]);
  const [topLoading, setTopLoading] = useState(false);
  const [topError, setTopError] = useState(null);
  const [similarChannelInput, setSimilarChannelInput] = useState('');
  const [similarChannels, setSimilarChannels] = useState([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [similarError, setSimilarError] = useState(null);
  const [player, setPlayer] = useState({ open: false, id: '', title: '' });

  const apiGet = async (path, params = {}) => {
    const base = getApiBase();
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, v);
    });
    const url = `${base}${path}${qs.toString() ? `?${qs.toString()}` : ''}`;
    const r = await fetch(url, { credentials: 'include' });
    const text = await r.text();
    let j;
    try { j = JSON.parse(text); } catch (_) {
      throw new Error(`Invalid response from ${path} (HTTP ${r.status})`);
    }
    if (!r.ok) {
      const msg = j?.details || j?.error || `HTTP ${r.status}`;
      throw new Error(msg);
    }
    return j;
  };

  const fetchPopular = async () => {
    setLoading(true); setError(null);
    try {
      const j = await apiGet('/api/google-ads/youtube/trends/most-popular', {
        region,
        maxResults: Math.min(maxResults, 50),
        categoryId,
      });
      const list = (j.items || []).map((it) => ({
        videoId: it.videoId,
        title: it.title,
        channelTitle: it.channelTitle,
        channelId: it.channelId,
        publishedAt: it.publishedAt,
        views: Number(it.statistics?.viewCount || 0),
        categoryId: it.categoryId,
      }));
      setItems(list);
    } catch (e) {
      setError(e?.message || 'Failed to load');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSearch = async () => {
    if (!q?.trim()) { setItems([]); return; }
    setLoading(true); setError(null);
    try {
      const j = await apiGet('/api/google-ads/youtube/trends/search', {
        q,
        region,
        maxResults: Math.min(maxResults, 50),
        categoryId,
      });
      const list = (j.items || []).map((it) => ({
        videoId: it.videoId,
        title: it.title,
        channelTitle: it.channelTitle,
        channelId: it.channelId,
        publishedAt: it.publishedAt,
        views: Number(it.statistics?.viewCount || 0),
        categoryId: it.categoryId,
      }));
      setItems(list);
    } catch (e) {
      setError(e?.message || 'Failed to search');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const j = await apiGet('/api/google-ads/youtube/trends/categories', { region });
      const list = (j.items || []).map((it) => ({ id: String(it.id), title: it.title || 'Unknown' }));
      // Ensure legacy 'Travel & Events' (id=19) exists in dropdown even if not assignable in some regions
      if (!list.find((c) => c.id === '19')) {
        list.push({ id: '19', title: 'Travel & Events' });
      }
      setCategories(list);
      if (categoryId && !list.find((c) => c.id === categoryId)) setCategoryId('');
    } catch (_) {
      // Non-fatal; dropdown will show All only
    }
  };

  const fetchTopChannels = async () => {
    setTopLoading(true); setTopError(null);
    try {
      const j = await apiGet('/api/google-ads/youtube/trends/top-channels', {
        region,
        categoryId,
        q: q && q.trim() ? q.trim() : '',
        maxResults: 50,
      });
      setTopChannels(j.items || []);
    } catch (e) {
      setTopError(e?.message || 'Failed to load top channels');
      setTopChannels([]);
    } finally {
      setTopLoading(false);
    }
  };

  useEffect(() => { if (tab === 'popular') fetchPopular(); }, [tab, region, maxResults, categoryId]);
  useEffect(() => { if (tab === 'search') { const t = setTimeout(fetchSearch, 400); return () => clearTimeout(t); } }, [tab, q, region, maxResults, categoryId]);
  useEffect(() => { fetchCategories(); }, [region]);
  useEffect(() => {
    if (tab !== 'channels') return;
    const t = setTimeout(fetchTopChannels, 300);
    return () => clearTimeout(t);
  }, [tab, region, categoryId, q]);

  const fetchSimilarChannels = async () => {
    const input = similarChannelInput?.trim();
    if (!input) { setSimilarChannels([]); setSimilarError('Kanal URL veya @handle girin'); return; }
    setSimilarLoading(true); setSimilarError(null);
    try {
      const j = await apiGet('/api/google-ads/youtube/trends/similar-channels', {
        channel: input,
        region,
        maxResults: 50,
      });
      setSimilarChannels(j.items || []);
    } catch (e) {
      const msg = e?.message || 'Hata';
      setSimilarError(msg === 'channel_not_found' ? 'Kanal bulunamadı' : msg === 'channel_required' ? 'Kanal girin' : msg);
      setSimilarChannels([]);
    } finally {
      setSimilarLoading(false);
    }
  };

  return (
    <div className="page-content" style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <img src="/youtubelogov2.svg" alt="YouTube" style={{ height: 26 }} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setTab('popular')} style={btnStyle(tab === 'popular')}>Regional Top</button>
          <button onClick={() => setTab('search')} style={btnStyle(tab === 'search')}>Search Topic</button>
          <button onClick={() => setTab('channels')} style={btnStyle(tab === 'channels')}>Top Channels</button>
          <button onClick={() => setTab('similar')} style={btnStyle(tab === 'similar')}>Benzer Kanallar</button>
          <button
            onClick={() => {
              if (tab === 'similar' && similarChannels.length > 0) {
                const exportData = similarChannels.map(ch => ({
                  channelTitle: ch.channelTitle || '',
                  channelId: ch.channelId || '',
                  channelUrl: ch.channelId ? `https://www.youtube.com/channel/${ch.channelId}` : '',
                  subscriberCount: ch.subscriberCount || 0,
                  videoCount: ch.videoCount || 0,
                  viewCount: ch.viewCount || 0,
                }));
                exportToCSV(exportData, `youtube_similar_channels_${new Date().toISOString().slice(0,10)}.csv`);
              } else if (tab === 'channels' && topChannels.length > 0) {
                const exportData = topChannels.map(ch => ({
                  channelTitle: ch.channelTitle || '',
                  channelId: ch.channelId || '',
                  channelUrl: ch.channelId ? `https://www.youtube.com/channel/${ch.channelId}` : '',
                  totalViews: ch.totalViews || 0,
                  videoCount: ch.videoCount || 0,
                  totalVideoCount: ch.totalVideoCount || 0,
                  lastPublishedAt: ch.lastPublishedAt || '',
                }));
                exportToCSV(exportData, `youtube_channels_${region}_${new Date().toISOString().slice(0,10)}.csv`);
              } else if (tab !== 'channels' && tab !== 'similar' && items.length > 0) {
                const exportData = items.map(it => ({
                  title: it.title || '',
                  videoId: it.videoId || '',
                  videoUrl: it.videoId ? `https://www.youtube.com/watch?v=${it.videoId}` : '',
                  channelTitle: it.channelTitle || '',
                  channelId: it.channelId || '',
                  views: it.views || 0,
                  publishedAt: it.publishedAt || '',
                  categoryId: it.categoryId || '',
                }));
                exportToCSV(exportData, `youtube_videos_${region}_${new Date().toISOString().slice(0,10)}.csv`);
              }
            }}
            disabled={(tab === 'similar' ? similarChannels.length === 0 : tab === 'channels' ? topChannels.length === 0 : items.length === 0)}
            style={{
              ...btnStyle(false),
              opacity: (tab === 'similar' ? similarChannels.length === 0 : tab === 'channels' ? topChannels.length === 0 : items.length === 0) ? 0.5 : 1,
              cursor: (tab === 'similar' ? similarChannels.length === 0 : tab === 'channels' ? topChannels.length === 0 : items.length === 0) ? 'not-allowed' : 'pointer',
            }}
            title="Export current list to CSV"
          >
            📥 Export
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <select value={region} onChange={(e) => setRegion(e.target.value)} style={inputStyle}>
          {REGIONS.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
        </select>
        <select value={categoryId} onChange={(e)=> setCategoryId(e.target.value)} style={inputStyle}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        <input type="number" min={4} max={50} step={2} value={maxResults} onChange={(e)=> setMaxResults(Number(e.target.value)||16)} style={inputStyle} />
        {(tab === 'search' || tab === 'channels') && (
          <input
            value={q}
            onChange={(e)=> setQ(e.target.value)}
            placeholder={tab === 'channels' ? 'Filter channels by topic (e.g., amerika turu)' : 'Search topic (e.g., skincare, football)'}
            style={{ ...inputStyle, minWidth: 260 }}
          />
        )}
        {tab === 'similar' && (
          <>
            <input
              value={similarChannelInput}
              onChange={(e) => setSimilarChannelInput(e.target.value)}
              placeholder="Kanal URL veya @handle (örn: https://youtube.com/@kanaladi)"
              style={{ ...inputStyle, minWidth: 320 }}
              onKeyDown={(e) => e.key === 'Enter' && fetchSimilarChannels()}
            />
            <button onClick={fetchSimilarChannels} disabled={similarLoading || !similarChannelInput?.trim()} style={btnStyle(false)}>
              {similarLoading ? 'Yükleniyor...' : 'Benzer Kanalları Bul'}
            </button>
          </>
        )}
      </div>

      {error && tab !== 'similar' && tab !== 'channels' && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</div>}
      {topError && tab === 'channels' && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{topError}</div>}
      {similarError && tab === 'similar' && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{similarError}</div>}
      {loading && tab !== 'similar' && tab !== 'channels' && <div style={{ marginTop: 20 }}>Loading...</div>}
      {topLoading && tab === 'channels' && <div style={{ marginTop: 20 }}>Loading...</div>}

      {!loading && tab !== 'channels' && tab !== 'similar' && (
        <div style={gridStyle}>
          {items.length === 0 && !error && (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#6b7280', padding: 24, border: '1px dashed #e5e7eb', borderRadius: 12 }}>
              {tab === 'popular' ? 'No items. Try another region or count.' : 'Type a keyword to see popular videos.'}
            </div>
          )}
          {items.map(it => (
            <button
              key={it.videoId}
              onClick={() => setPlayer({ open: true, id: it.videoId, title: it.title })}
              style={{ ...cardStyle, cursor: 'pointer', textAlign: 'left' }}
            >
              <img alt={it.title} src={`https://i.ytimg.com/vi/${it.videoId}/hqdefault.jpg`} style={{ width: '100%', height: 180, objectFit: 'cover' }} />
              <div style={{ padding: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 4, lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{it.title}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{it.channelTitle}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>{formatCompact(it.views)} views · {it.publishedAt ? new Date(it.publishedAt).toLocaleDateString() : ''}</div>
                {it.categoryId && (
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                    {(categories.find(c => c.id === String(it.categoryId)) || {}).title || ''}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {!topLoading && tab === 'channels' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
          {topChannels.length === 0 && !topError && (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#6b7280', padding: 24, border: '1px dashed #e5e7eb', borderRadius: 12 }}>
              No channels. Try another region, category, or keyword.
            </div>
          )}
          {topChannels.map((ch, idx) => (
            <a key={idx} href={ch.channelId ? `https://www.youtube.com/channel/${ch.channelId}` : '#'} target="_blank" rel="noreferrer" style={cardStyle}>
              <div style={{ padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <img src={ch.thumbUrl || '/youtube.svg'} alt="avatar" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', background: '#f3f4f6' }} />
                  <div style={{ fontWeight: 700, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch.channelTitle || ch.channelId}</div>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>Total videos: {formatCompact(ch.totalVideoCount ?? 0)}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>{formatCompact(ch.totalViews)} total views</div>
                {ch.lastPublishedAt && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Latest: {new Date(ch.lastPublishedAt).toLocaleDateString()}</div>}
              </div>
            </a>
          ))}
        </div>
      )}

      {tab === 'similar' && (
        <div style={{ marginTop: 16 }}>
          {similarLoading && <div style={{ marginBottom: 12 }}>Yükleniyor...</div>}
          {!similarLoading && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
              {similarChannels.length === 0 && (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#6b7280', padding: 24, border: '1px dashed #e5e7eb', borderRadius: 12 }}>
                  Bir kanal URL'si veya @handle girin ve "Benzer Kanalları Bul" butonuna tıklayın.
                </div>
              )}
              {similarChannels.map((ch, idx) => (
                <a key={ch.channelId || idx} href={ch.channelId ? `https://www.youtube.com/channel/${ch.channelId}` : '#'} target="_blank" rel="noreferrer" style={cardStyle}>
                  <div style={{ padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <img src={ch.thumbUrl || '/youtube.svg'} alt="avatar" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', background: '#f3f4f6' }} />
                      <div style={{ fontWeight: 700, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch.channelTitle || ch.channelId}</div>
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{formatCompact(ch.subscriberCount)} abone</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{formatCompact(ch.videoCount)} video · {formatCompact(ch.viewCount)} görüntülenme</div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
      {player.open && (
        <div className="fixed inset-0 z-50" style={{ position: 'fixed', inset: 0, zIndex: 50 }}>
          <div onClick={() => setPlayer({ open: false, id: '', title: '' })} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} />
          <div style={{ position: 'relative', maxWidth: 960, margin: '40px auto', background: '#000', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', color: '#fff' }}>
              <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{player.title}</div>
              <button onClick={() => setPlayer({ open: false, id: '', title: '' })} style={{ color: '#fff' }}>✕</button>
            </div>
            <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%' }}>
              <iframe
                title={player.title}
                src={`https://www.youtube.com/embed/${player.id}?autoplay=1&rel=0&modestbranding=1`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function btnStyle(active) { return { padding: '8px 12px', borderRadius: 10, border: '1px solid #e5e7eb', background: active ? 'black' : 'white', color: active ? 'white' : 'black', cursor: 'pointer' }; }
const inputStyle = { padding: '8px 10px', borderRadius: 10, border: '1px solid #e5e7eb' };
const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 };
const cardStyle = { display: 'block', border: '1px solid #f1f1f1', boxShadow: '0 4px 14px rgba(0,0,0,0.06)', borderRadius: 14, overflow: 'hidden', textDecoration: 'none', color: 'inherit', background: 'white' };
function formatCompact(v){ if (!v) return '0'; try { return Intl.NumberFormat('en', { notation: 'compact' }).format(Number(v)); } catch(_) { return String(v); } }

function exportToCSV(data, filename) {
  if (!data || data.length === 0) return;
  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(','),
    ...data.map(row => headers.map(h => {
      const val = row[h] ?? '';
      // Escape quotes and wrap in quotes if contains comma/quote/newline
      const str = String(val).replace(/"/g, '""');
      return /[,"\n]/.test(str) ? `"${str}"` : str;
    }).join(','))
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
